import {getWithIDToken, IMSTokenParams, postWithIdToken, putWithIdToken} from "@utils/http/MSAuthAPI";
import {Country} from '@utils/constants/localisation';
import {ISubscriptionDetailPanel} from '@components/panels/SubscriptionDetailsPanel';
import {ISubscriptionSummaryItem} from '@components/cards/SubscriptionSummary/SubscriptionSummary';
import {truncateToTwoDecimals} from '@utils/misc/functions';


// We can use alternateHost to point to localhost for testing
// const alternateHost = 'http://localhost:5000';
const alternateHost = '';

type YYYY = string;
type MM = string;
type DD = string;
type DateFormat = `${YYYY}-${MM}-${DD}`;


export type AddonDetails = {
    id: string;
    name: string;
    price: number;
    pricingModel: string;
};

// Long fee name constants (mirrors Python CancellationFee.LongFeeNames)
export const LongFeeNames = {
    WeeklyVehicleChargeFee: 'Flexi Own Cancellation Fee',
    WeeklyVehicleChargeFee_2_1: '2.1 Flexi Own Cancellation Fee',
    NoticePeriodFee: 'Notice Period Fee',
    PaymentHolidayGapFee: 'Payment Holiday Fee',
    AncillaryFee: 'Ancillary Fee',
    NbpEarlyBreakFee: 'Non-Break Period Early Break Fee',
} as const;

export type LongFeeName = typeof LongFeeNames[keyof typeof LongFeeNames];


export type CancellationChargesV2QueryParams = {
    date_submitted_notice: DateFormat;
    contract_end_date: DateFormat;
    country: Country;
    cancellation_type: string;
    cancellation_category: string;
    is_vehicle_remarketable: boolean | null;
};

export type CancellationFeeBase = {
    fee: number;
    cb_addon_id: string;
    quantity: number;
    long_fee_name: LongFeeName | string;
};

export type NoticePeriodFee = CancellationFeeBase & {
    days_charged: number;
    daily_subscription_fee: number;
};

export type FOV2WeeklyVehicleFee = CancellationFeeBase & {
    weekly_a1_component_fee: number;
    termination_weeks_multiplier: number;
    vehicle_remarketing_fee: number;
    weeks_payout_protection_fee_paid: number;
};

export type FOV2_1WeeklyVehicleFee = FOV2WeeklyVehicleFee & {
    initial_contribution: number | null;
    term_length_weeks: number | null;
    term_remaining_weeks: number | null;
};

export type PaymentHolidayGapFee = CancellationFeeBase & {
    total_ph_weeks_taken: number;
    total_ph_contribution: number;
    total_ph_topups: number;
    total_ph_subscription_fee: number;
};

export type AncillaryFee = CancellationFeeBase & {
    unit_price: number;
    cb_addon_name: string;
};

export type NbpEarlyBreakFee = CancellationFeeBase & {
    // Both null when the charge fires with fee=0 (e.g. cancellation_type whitelist failed
    // or no active NBP at cancellation date). Populated when the fee is non-zero so the
    // FE can explain which NBP commitment is being broken.
    triggering_nbp_type: string | null;
    triggering_nbp_end_date: string | null;
};

export type SpecificFeeTypes = NoticePeriodFee | FOV2WeeklyVehicleFee | FOV2_1WeeklyVehicleFee | PaymentHolidayGapFee | AncillaryFee | NbpEarlyBreakFee;

export type CancellationChargesV2Response = {
    charges: SpecificFeeTypes[];
    warnings?: string[]; // Python default is [] — ensure callers set default if needed
};


// Type mirroring the server response structure
type CouponSvDetails = {
    id: string;
    discount_amount: number
    // The weekly amount the coupon ID declares, in minor currency. Null when the ID follows no
    // recognised naming convention.
    weekly_discount_incl_tax: number | null
};


// Type to map the raw response structure to
type CouponDetails = {
    id: string;
    discountValue: string;
    durationType: string;
};


// The list endpoint returns a different shape to getCouponById - only the id and the amounts
export type CouponListItem = {
    id: string;
    // Chargebee's own figure, NET of tax. Almost never the one to show a customer - see
    // weeklyDiscountInclTax.
    discountAmount: number;
    // The weekly discount the coupon ID declares, in minor currency, INCLUSIVE of tax. This is the
    // customer-facing figure and matches the Zoho subscription's Total_Weekly_Discount. Null when
    // the ID declares no value.
    weeklyDiscountInclTax: number | null;
};


// getSubscriptionById returns the panel fields plus a few extras that individual journeys need.
export type SubscriptionDetails = ISubscriptionDetailPanel & {
    vehicleId: string;
    // Zoho's current weekly rental fee and dynamic pricing discount, both as returned by Zoho.
    // Null when the subscription has no value set.
    weeklyRentalFee: string | null;
    totalWeeklyDiscount: string | null;
};

export type processCancellationQueryParams = {
    country: Country;

    // On the backend, the `zoho_subscription_id` is parsed out of the URL path param and into the request model.
    // On the FE, its sent in the URL, so its not required as a param in this type.

    cancellation_date: string;
    charges: SpecificFeeTypes[];

    fleetio_vehicle_id: number;
    vehicle_odometer: number;
    vehicle_return_status_id: number;

    // GB only: the Fleetio `vehicle_location` the returning vehicle is being left at.
    // null for AU, which models location through Fleetio groups instead (TP-6849).
    vehicle_location?: string | null;

    cancellation_type: string;
    cancellation_reason_outcome: string;
    cancellation_reason_reason: string;
    cancellation_additional_info: string;

    customer_returning_date?: string | null;
    add_restriction_to_account: boolean;

    vehicle_remarketable: boolean | null;

    csr_email: string;
};

export type ChargebeeCancellationResult = {
    success: boolean;
    chargebee_subscription_url: string
}

type FleetioResult = {
    success: boolean;
    fleetio_vehicle_description: string;
    fleetio_vehicle_url: string;
};

type ZohoSubscriptionResult = {
    success: boolean;
    zoho_subscription_url: string;
};

type ZohoCancellationResult = {
    success: boolean;
    zoho_cancellation_url: string | null;
};

export type ProcessCancellationResponse = {
     chargebee_result: ChargebeeCancellationResult;
     fleetio_result: FleetioResult;
     zoho_cancellation_result: ZohoCancellationResult;
     zoho_subscription_result: ZohoSubscriptionResult;
};

/**
 * Interface SHALL be updated each time a new method is added.
 */
interface ISubscriptionService {
    getSubscriptions(customerId: string, country: Country, IDTokenParams: IMSTokenParams): Promise<ISubscriptionSummaryItem[]>,
    getSubscriptionById(subscriptionId: string, country: Country, IDTokenParams: IMSTokenParams): Promise<SubscriptionDetails | {}>,
    getAdditionalDriverSubscriptionById(subscriptionId: string, country: Country, IDTokenParams: IMSTokenParams): Promise<ISubscriptionDetailPanel | {}>,
    getCancellationCharges(qParams: object, subscriptionId: string, fleetioVehicleID:string, IDTokenParams: IMSTokenParams): Promise<any>,
    getCancellationCharges_v2(qParams: CancellationChargesV2QueryParams, subscriptionId: string, IDTokenParams: IMSTokenParams): Promise<CancellationChargesV2Response>,
    getAddons(qParams: object, IDTokenParams: IMSTokenParams): Promise<AddonDetails[]>,
    getCoupons(qParams: object, IDTokenParams: IMSTokenParams): Promise<CouponListItem[]>,
    getCouponById(couponId: string, country: Country, IDTokenParams: IMSTokenParams): Promise<CouponDetails>,
    putCancellationDetails(subscriptionId: string, payload: any, country: Country, IDTokenParams: IMSTokenParams): Promise<any>,
    putProcessCancellation_v2(qParams: processCancellationQueryParams, subscriptionId: string,  IDTokenParams: IMSTokenParams): Promise<ProcessCancellationResponse>,
    getTestDrives(customerId: string, country: Country, IDTokenParams: IMSTokenParams): Promise<any>,
    endTestDrive(payload: any, potential_id: string, customerId: string, country: Country, IDTokenParams: IMSTokenParams): Promise<any>,

}


// TODO: enhance service with error handling methods; maybe an interface?
const SubscriptionService: ISubscriptionService = {

    /**
     * Given a customer id and a country code, call the backend API to get all subscriptions hold by the customer.
     * A customer can have from 0 to S subscriptions, so the retrieved list can be empty as well.
     *
     * @param customerId
     * @param country
     * @returns List comprising a set of attributes per subscriptions
     */
    async getSubscriptions(customerId: string, country: Country, IDTokenParams: IMSTokenParams) {
        const convertDateFormat = (dateValue: DateFormat) => (
            new Date(dateValue).toLocaleString('en-GB', {day: 'numeric', month: 'long', year: 'numeric'})
        );

        const resp = await getWithIDToken(
            `/v1/customers/${customerId}/subscriptions`,
            IDTokenParams,
            {country: country}
        );
        if (resp.status === 500) {
            return [];
        }
        const processedData = resp.data.map((subs: any) => {
            return {
                subsId: subs.id,
                startDate: convertDateFormat(subs.startDate),
                carDetails: `${subs.Make || '-'} ${subs.Model || '-'}`,
                registration: subs.Vehicle_Registration || '-',
                status: subs.Subscription_Status,
                plan: subs.Plan || '-',
                noticeGiven: subs.Date_Submitted_Notice,
                vehicleId: subs.Fleetio_Vehicle_ID,
                onboardVersion: subs.Onboard_Version,
                tempVehicleId: subs.Temporary_Vehicle_ID || null,
                tempVehicleReg: subs.Temporary_Vehicle_Reg || null,
            };
        });
        return processedData;
    },

    /**
     * Given a subscription id, retrieve a set of attributes for it.
     *
     * @param subscriptionId
     * @param country
     * @returns Object holding the subscription's attributes
     */
    async getSubscriptionById(subscriptionId: string, country: Country, IDTokenParams: IMSTokenParams) {
        const convertDateFormat = (dateValue: DateFormat) => (
            new Date(dateValue).toLocaleString('en-GB', {day: 'numeric', month: 'long', year: 'numeric'})
        );

        const resp = await getWithIDToken(
            `/v1/subscriptions/${subscriptionId}`,
            IDTokenParams,
            {country: country}
        );
        if (resp.status === 500) {
            return {};
        }
        const processedData = {
            rentalAgreement: resp.data.Rental_Agreement_Number1,
            startDate: convertDateFormat(resp.data.startDate),  // backendAPI field denoting start date (Zoho field propagated)
            registration: resp.data.Vehicle_Registration,
            plan: resp.data.Plan,
            termLength: resp.data.Flexi_Own_plan_length_in_years,
            make: resp.data.Make,
            model: resp.data.Model,
            email: resp.data.Email,
            noticeGiven: resp.data.Date_Submitted_Notice,
            vehicleId: resp.data.Fleetio_Vehicle_ID,
            weeklyRentalFee: resp.data.Rental_Fee ?? null,
            totalWeeklyDiscount: resp.data.Total_Weekly_Discount ?? null,
        };
        return processedData;
    },

    /**
     * Given a subscription id, retrieve a set of attributes for the additional driver journey.
     *
     * @param subscriptionId
     * @param country
     * @returns Object holding the subscription's attributes
     */
    async getAdditionalDriverSubscriptionById(subscriptionId: string, country: Country, IDTokenParams: IMSTokenParams) {

        const resp = await getWithIDToken(
            `/v1/subscriptions/${subscriptionId}`,
            IDTokenParams,
            {country: country}
        );
        if (resp.status === 500) {
            return {};
        }
        const processedData = {
            firstName: resp.data.Additional_Driver_First_Name,
            lastName: resp.data.Additional_Driver_Last_Name,
            licenceNumber: resp.data.Additional_Driver_License_Number,
            licenceState: resp.data.Additional_Driver_State,
            expiryDate: resp.data.Additional_Driver_License_Expiry_Date,
            dob: resp.data.Additional_Driver_Date_of_Birth,

        };

        return processedData;
    },


    async getCancellationCharges(qParams: object, subscriptionId: string, fleetioVehicleID, IDTokenParams: IMSTokenParams) {
        const resp = await getWithIDToken(
            `/v1/subscriptions/${subscriptionId}/cancellation-charges`,
            IDTokenParams,
            {...qParams}
        );
        if (resp.status >= 500) {
            return {};
        }

        let processedData = {
            state: resp.data['state'],
            dailyFee: truncateToTwoDecimals(resp.data['daily_fee'] || 0),
            weeklyFee: truncateToTwoDecimals(resp.data['weekly_fee'] || 0),
            noticePeriodCharges: truncateToTwoDecimals(resp.data["notice_period_charges"]["charge"] || 0),
            noticeDaysToCharge: resp.data['notice_period_charges']['days_to_charge'] || 0,
            minimumTermCharges: truncateToTwoDecimals(resp.data["minimum_term_charges"]["charge"] || 0),
            acceptanceAndRemarketing: truncateToTwoDecimals(resp.data["minimum_term_charges"]["charge"] || 0),
            drivenDistance: truncateToTwoDecimals(resp.data["minimum_term_charges"]["driven_distance"] || 0),
            minimumDaysToCharge: resp.data['minimum_term_charges']['days_to_charge'] || 0,
            startOdometer: truncateToTwoDecimals(resp.data["minimum_term_charges"]["start_odometer"] || 0),
            finishOdometer: truncateToTwoDecimals(resp.data["minimum_term_charges"]["finish_odometer"] || 0),
            usageCharge: truncateToTwoDecimals(resp.data["minimum_term_charges"]["usage_charge"] || 0),
            adminFee: truncateToTwoDecimals(resp.data["minimum_term_charges"]["admin_fee"] || 0),
            phSubscriptionCharge: truncateToTwoDecimals(resp.data["payment_holiday_charges"]["ph_subscription_fee"] || 0),
            phWeeksTaken: truncateToTwoDecimals(resp.data["payment_holiday_charges"]["number_of_weeks_taken"] || 0),
            phTotalContribution: truncateToTwoDecimals(resp.data["payment_holiday_charges"]["total_contributions"] || 0),
            phTotalCharge: truncateToTwoDecimals(resp.data["payment_holiday_charges"]["charge"] || 0),
            // NBP early-break fee. Flat fee; metadata describes which NBP commitment is being
            // broken so the CSR can explain the charge. fee=0 when out of scope (wrong
            // cancellation_type, non-FO plan, no active NBP at cancellation date).
            nbpEarlyBreakCharge: {
                fee: truncateToTwoDecimals(resp.data["nbp_early_break_charge"]?.["fee"] || 0),
                triggeringNbpType: resp.data["nbp_early_break_charge"]?.["triggering_nbp_type"] || null,
                triggeringNbpEndDate: resp.data["nbp_early_break_charge"]?.["triggering_nbp_end_date"] || null,
            },
            errors: resp.data["errors"] || []
        };

        if (resp.data["payment_holiday_charges"]) {
            processedData.phWeeksTaken = resp.data["payment_holiday_charges"]['number_of_weeks_taken'];
            processedData.phTotalContribution = truncateToTwoDecimals(resp.data["payment_holiday_charges"]["total_contributions"]);
            processedData.phTotalCharge = truncateToTwoDecimals(resp.data["payment_holiday_charges"]["charge"]);
            processedData.phSubscriptionCharge = truncateToTwoDecimals(resp.data["payment_holiday_charges"]["ph_subscription_fee"]);

        }

        return processedData;
    },

    /**
     * Get cancelation charges via v2 endpoint.
     * Currently, supports FO 2.0 plans
     *
     * @param qParams - Query parameters containing date_submitted_notice and contract_end_date (YYYY-MM-DD format)
     * @param subscriptionId - The Zoho subscription ID
     * @param IDTokenParams - MS authentication token parameters
     * @returns Processed cancellation charges data
     */
    async getCancellationCharges_v2(qParams: CancellationChargesV2QueryParams, subscriptionId: string, IDTokenParams: IMSTokenParams): Promise<CancellationChargesV2Response> {
        const resp = await getWithIDToken(
            `/v1/subscriptions/${subscriptionId}/cancellation-charges-v2`,
            IDTokenParams,
            {...qParams}
        );

        // Check for error responses
        if (resp.status >= 500) {
            throw new Error('Server error when fetching cancellation charges');
        }

        if (resp.status >= 400) {
            throw new Error(`Client error when fetching cancellation charges: ${resp.status}`);
        }

        // Validate that we received data
        if (!resp.data) {
            throw new Error('No data returned from cancellation charges API');
        }

        const processedData: CancellationChargesV2Response = {
            charges: resp.data.data.charges || [],
            warnings: resp.data.data.warnings || []
        };
        return processedData;
    },

    async getAddons(qParams: object, IDTokenParams: IMSTokenParams): Promise<AddonDetails[]> {
        const resp = await getWithIDToken(
            `/v1/addons`,
            IDTokenParams,
            {...qParams}
        );
        if (resp.status === 500) {
            return [];
        }

        const processedData = resp.data.map((record: any) => ({
            id: record.id || record.addon?.id,
            name: record.name || record.addon?.name,
            price: record.price || record.addon?.price,
            pricingModel: record.pricing_model || record.addon.pricing_model
        }));
        return processedData;
    },

    async getCoupons(qParams: object, IDTokenParams: IMSTokenParams): Promise<CouponListItem[]> {
        const resp = await getWithIDToken(
            `/v1/coupons`,
            IDTokenParams,
            {...qParams}
        );

        const processedData = resp.data.data.map((record: CouponSvDetails) => ({
            id: record.id,
            discountAmount: record.discount_amount,
            weeklyDiscountInclTax: record.weekly_discount_incl_tax ?? null
        }));

        return processedData;
    },

    async getCouponById(couponId: string, country: Country, IDTokenParams: IMSTokenParams): Promise<CouponDetails> {
        const resp = await getWithIDToken(
            `${alternateHost}/v1/coupons/${couponId}`,
            IDTokenParams,
            {country}
        );
        console.log("resp", resp);

        return {
            id: resp.data.data.id,
            discountValue: resp.data.data.discount_amount,
            durationType: resp.data.data.duration_type,
        };
    },

    async putCancellationDetails(subscriptionId: string, payload: any, country: Country, IDTokenParams: IMSTokenParams) {
        const resp = await putWithIdToken(
            `v1/subscriptions/${subscriptionId}/cancel`,
            IDTokenParams,
            {country},
            payload
        );
        //return necessary info
        const processedData = {
            fleetioLink: resp.data.fleetio_url,
            zohoLink: resp.data.zoho_url,
            chargebeeURL: resp.data.cb_url,
        };

        return processedData;
    },

    async putProcessCancellation_v2(qParams: processCancellationQueryParams, subscriptionId: string, IDTokenParams: IMSTokenParams): Promise<ProcessCancellationResponse> {
        console.debug("Calling putProcessCancellation_v2", qParams, subscriptionId);

        const resp = await putWithIdToken(
            `v1/subscriptions/${subscriptionId}/cancel-v2`,
            IDTokenParams,
            {},
            qParams
        );

        console.log("putProcessCancellation_v2 response data", resp.data);

        return {
            chargebee_result: {
                success: resp.data.chargebee_result.success,
                chargebee_subscription_url: resp.data.chargebee_result.chargebee_subscription_url
            },
            fleetio_result: {
                success: resp.data.fleetio_result.success ,
                fleetio_vehicle_description: resp.data.fleetio_result.fleetio_vehicle_description,
                fleetio_vehicle_url: resp.data.fleetio_result.fleetio_vehicle_url,
            },
            zoho_cancellation_result: {
                success: resp.data.zoho_cancellation_result.success,
                zoho_cancellation_url: resp.data.zoho_cancellation_result.zoho_cancellation_url
            },
            zoho_subscription_result: {
                success: resp.data.zoho_subscription_result.success,
                zoho_subscription_url: resp.data.zoho_subscription_result.zoho_subscription_url,
            },
        };
    },

    /**
     * Given a customer id and a country code, call the backend API to get all potentials held by the customer.
     * A customer can have from 0 to S potentials, so the retrieved list can be empty as well.
     *
     * @param customerId
     * @param country
     * @returns List comprising a set of attributes per potential
     */
    async getTestDrives(customerId: string, country: Country, IDTokenParams: IMSTokenParams) {

        const resp = await getWithIDToken(
            `/v1/customers/${customerId}/potentials`,
            IDTokenParams,
            {country: country}
        );
        if (resp.status === 500) {
            return [];
        }
        const processedData = resp.data.map((potential: any) => {
            return {
                contactRole: potential.Contact_Role,
                id: potential.id,
                endDatetime: potential.Test_Drive_End,
                startDatetime: potential.Test_Drive_Start,
                vehicle: potential.Test_Drive_Vehicle,
                registration: potential.Test_Drive_Vehicle_Registration,
                vehicleId: potential.Fleetio_Vehicle_ID,
            }
        });
        return processedData;
    },

    async endTestDrive(payload: any, customerId: string, potentialId: string, country: Country, IDTokenParams: IMSTokenParams) {
        const resp = await postWithIdToken(
            `v1/customers/${customerId}/potentials/${potentialId}`,
            IDTokenParams,
            {country},
            payload
        );
        //return necessary info
        const processedData = {
            fleetioLink: resp.data.fleetio_link,
            zohoLink: resp.data.zoho_link,
            vehicle: resp.data.vehicle,
        };

        return processedData;
    },
};


export default SubscriptionService;
